import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableType } from '@common/ts/enums';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { getPaginationParamPage } from '@/utils/urlParams';
import {
  TRANSCRIPTS_FILTER_PREFIX,
  getTranscriptFilterParamName,
  isFilterableColumnId,
  getFilterableColumnsCatalog,
  getColumnDisplayLabel,
} from '@/hooks/useTranscriptsFilters';
import { AddFilterPicker } from './AddFilterPicker';
import { FilterPill } from './FilterPill';

interface TranscriptsFilterBarProps {
  /**
   * Metadata keys for columns currently rendered in the table. Passed
   * through to the picker catalog — source of truth for what metadata
   * the project actually has.
   */
  visibleMetadataKeys: string[];
}

const clearAllTranscriptPages = (next: URLSearchParams) => {
  next.delete(getPaginationParamPage(TableType.TRANSCRIPTS));
  next.delete(getPaginationParamPage(TableType.INCOMPLETE_TRANSCRIPTS));
  next.delete(getPaginationParamPage(TableType.SCREENED_OUT_TRANSCRIPTS));
  next.delete(getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS));
};

export default function TranscriptsFilterBar({ visibleMetadataKeys }: TranscriptsFilterBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useTranslation();

  // Columns the user picked in this session that don't yet have a value
  // in the URL. Combined with URL-derived columns to decide what pills
  // to render. See architect-review-notes in the PR description.
  const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set());

  // Tracks the most recently added column so FilterPill can auto-focus
  // exactly once. Read + cleared in FilterPill on mount.
  const lastAddedRef = useRef<string | null>(null);

  const catalog = useMemo(
    () => getFilterableColumnsCatalog(visibleMetadataKeys),
    [visibleMetadataKeys],
  );

  // URL-derived active column IDs. Empty values are ignored per useTranscriptsFilters
  // but here we include any key that appears at all (even empty) so back/forward
  // into a URL with empty values shows the pill.
  const urlColumnIds = useMemo(() => {
    const ids: string[] = [];
    searchParams.forEach((_value, key) => {
      if (!key.startsWith(TRANSCRIPTS_FILTER_PREFIX)) return;
      const columnId = key.slice(TRANSCRIPTS_FILTER_PREFIX.length);
      if (!isFilterableColumnId(columnId)) return;
      ids.push(columnId);
    });
    return ids;
  }, [searchParams]);

  // Displayed = union of URL and pendingAdds. Dedup while preserving URL order
  // first (restores stable layout on refresh).
  const displayedColumnIds = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    urlColumnIds.forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    });
    pendingAdds.forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    });
    return out;
  }, [urlColumnIds, pendingAdds]);

  const activeColumnIdSet = useMemo(
    () => new Set(displayedColumnIds),
    [displayedColumnIds],
  );

  const handlePick = useCallback((columnId: string) => {
    lastAddedRef.current = columnId;
    setPendingAdds((prev) => {
      if (prev.has(columnId)) return prev;
      const next = new Set(prev);
      next.add(columnId);
      return next;
    });
  }, []);

  const handleRemove = useCallback(
    (columnId: string) => {
      setPendingAdds((prev) => {
        if (!prev.has(columnId)) return prev;
        const next = new Set(prev);
        next.delete(columnId);
        return next;
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(getTranscriptFilterParamName(columnId));
          clearAllTranscriptPages(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleClearAll = useCallback(() => {
    setPendingAdds(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const keysToDelete: string[] = [];
        next.forEach((_v, key) => {
          if (key.startsWith(TRANSCRIPTS_FILTER_PREFIX)) keysToDelete.push(key);
        });
        keysToDelete.forEach((key) => next.delete(key));
        clearAllTranscriptPages(next);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Consume the ref once per render cycle — the pill that matches will
  // focus and we clear the ref so we don't re-focus on later renders.
  const shouldAutoFocus = (columnId: string) => {
    if (lastAddedRef.current !== columnId) return false;
    lastAddedRef.current = null;
    return true;
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 mb-3">
      <span className="text-[10px] text-neutral-400 uppercase tracking-wide mr-1">
        {lang.get('msg.tableSearch') || 'Table search'}
      </span>
      <AddFilterPicker
        columns={catalog}
        activeColumnIds={activeColumnIdSet}
        onPick={handlePick}
      />
      {displayedColumnIds.map((columnId) => (
        <FilterPill
          key={columnId}
          columnId={columnId}
          label={getColumnDisplayLabel(columnId)}
          autoFocus={shouldAutoFocus(columnId)}
          onRemove={handleRemove}
        />
      ))}
      {displayedColumnIds.length >= 2 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
        >
          {lang.get('msg.clearAll') || 'Clear all'}
        </Button>
      )}
    </div>
  );
}
